// =============================================================================
// EquipmentRegisterTab — IT & phones, Test equipment, Radios & other
// (mig 429, 8 Oct 2026)
// =============================================================================
// James, 8 Oct 2026: "Radios don't really have a place, nor do our test
// equipment or laptops and mobile phones ... add to these lists from AP as
// that's the input flow, even just the line and we back fill information."
//
// These three tabs on /assets read company_assets — the same list AP coding
// feeds and the insurance register reads — filtered by category. A row that
// arrived as just a line carries details_pending and shows "Fill in" until
// someone gives it a serial, a model or a holder and saves.
//
// 8 Oct 2026 (mig 432) — "Kept at" is a vehicle from the fleet list, the
// depot, or typed (KeptAtField). James: "could be assigned to a person or
// a car."
//
// Managers and above only: company_assets is a manager-read table (mig 351).
// =============================================================================

import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, X, Gauge, FileText, Upload } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Button, Spinner, EmptyState, Field, Input } from "@/components/ui";
import { fmtDate, daysFromNow } from "@/lib/dates";
import KeptAtField, { vehicleLabel, type KeptAt } from "./KeptAtField";

export type EquipmentTabKey = "eq_it" | "eq_test" | "eq_other";

export const EQUIPMENT_CATEGORIES: { value: string; label: string; tab: EquipmentTabKey }[] = [
  { value: "it",             label: "IT (laptop, tablet, screen)", tab: "eq_it" },
  { value: "phone",          label: "Mobile phone",                tab: "eq_it" },
  { value: "test_equipment", label: "Test equipment (needs calibration — RF monitors, meters, OTDR, torque)", tab: "eq_test" },
  { value: "radio",          label: "Radio",                       tab: "eq_other" },
  { value: "other",          label: "Other equipment",             tab: "eq_other" },
];
const CAT_LABEL: Record<string, string> = { it: "IT", phone: "Phone", test_equipment: "Test", radio: "Radio", other: "Other" };

interface Row {
  asset_id: string; asset_code: string | null; category: string; description: string;
  make_model: string | null; serial_number: string | null; location: string | null;
  assigned_to_employee_id: string | null; calibration_due: string | null;
  calibration_interval_months?: number | null; last_calibrated_on?: string | null; last_calibration_result?: string | null;
  is_active: boolean; details_pending: boolean; notes: string | null;
  purchase_date: string | null; supplier_name: string | null; ap_invoice_id: string | null;
  kept_in_vehicle_id: string | null;
  vehicle: { vehicle_code: string | null; rego: string | null; make: string | null; model: string | null } | null;
}
const keptAt = (r: Row) => (r.kept_in_vehicle_id ? vehicleLabel(r.vehicle) || "Vehicle" : r.location) || "";
interface Emp { employee_id: string; first_name: string | null; last_name: string | null }

const COLS = "asset_id, asset_code, category, description, make_model, serial_number, location, assigned_to_employee_id, calibration_due, calibration_interval_months, last_calibrated_on, last_calibration_result, is_active, details_pending, notes, purchase_date, supplier_name, ap_invoice_id, kept_in_vehicle_id, vehicle:vehicles(vehicle_code, rego, make, model)";

export default function EquipmentRegisterTab({ tab, search, canEdit, employees, userId }: {
  tab: EquipmentTabKey; search: string; canEdit: boolean; employees: Emp[]; userId: string | null;
}) {
  const qc = useQueryClient();
  const cats = useMemo(() => EQUIPMENT_CATEGORIES.filter(c => c.tab === tab), [tab]);
  const [editing, setEditing] = useState<Row | "new" | null>(null);
  const [calFor, setCalFor] = useState<Row | null>(null);   // mig 479 — log a calibration
  const [showInactive, setShowInactive] = useState(false);

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["assets", "equipment_register"],
    enabled: canEdit,
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await (supabase as any).from("company_assets").select(COLS).order("asset_code");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const empName = (id: string | null) => {
    const e = employees.find(x => x.employee_id === id);
    return e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : "";
  };

  if (!canEdit) return <Card><div className="p-6"><EmptyState title="Managers only" description="This list is kept by the office." /></div></Card>;
  if (isLoading) return <div className="py-10 flex justify-center"><Spinner /></div>;
  if (error) return <Card><div className="p-4 text-[13px] text-danger">Could not load the list: {(error as any)?.message ?? "unknown error"}</div></Card>;

  const mine = rows.filter(r => cats.some(c => c.value === r.category));
  const q = search.trim().toLowerCase();
  const shown = mine.filter(r => (showInactive || r.is_active) && (!q ||
    [r.asset_code, r.description, r.make_model, r.serial_number, keptAt(r), empName(r.assigned_to_employee_id), CAT_LABEL[r.category]]
      .some(x => (x ?? "").toLowerCase().includes(q))));
  const toFill = mine.filter(r => r.details_pending && r.is_active).length;
  const inactive = mine.filter(r => !r.is_active).length;
  const showCal = tab === "eq_test";
  const refresh = () => qc.invalidateQueries({ queryKey: ["assets", "equipment_register"] });

  async function assign(r: Row, empId: string) {
    const { error } = await (supabase as any).from("company_assets")
      .update({ assigned_to_employee_id: empId || null, updated_by: userId }).eq("asset_id", r.asset_id);
    if (error) { alert(error.message); return; }
    refresh();
  }

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <Button icon={Plus} onClick={() => setEditing("new")}>Add item</Button>
        {toFill > 0 && (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-warn">
            <AlertTriangle size={13} /> {toFill} item{toFill === 1 ? "" : "s"} came in as just a line — details still to fill in
          </span>
        )}
        {inactive > 0 && (
          <label className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-text-muted">
            <input type="checkbox" className="accent-accent" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show {inactive} no longer in use
          </label>
        )}
      </div>

      {shown.length === 0 ? (
        <Card><div className="p-6"><EmptyState title="Nothing on this list yet" description="Add an item here, or tick 'add to the asset list' when you approve the purchase in AP." /></div></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b border-line">
                {["Code", "Item", "Make / model", "Serial", ...(showCal ? ["Calibration due"] : []), "Who has it", "Kept at", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {shown.map((r, i) => {
                  const days = r.calibration_due ? daysFromNow(r.calibration_due) : null;
                  return (
                    <tr key={r.asset_id} className={`${i % 2 ? "bg-ink/40" : ""} ${i < shown.length - 1 ? "border-b border-line" : ""} ${r.is_active ? "" : "opacity-60"}`}>
                      <td className="px-4 py-3 font-mono text-accent text-[12px]">
                        <button type="button" className="hover:underline" onClick={() => setEditing(r)}>{r.asset_code ?? "—"}</button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{r.description}</span>
                          {cats.length > 1 && <Pill small label={CAT_LABEL[r.category] ?? r.category} />}
                          {r.details_pending && <Pill small tone="warning" label="Fill in" />}
                          {!r.is_active && <Pill small label="Not in use" />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{r.make_model || "—"}</td>
                      <td className="px-4 py-3 font-mono text-[12px]">{r.serial_number || "—"}</td>
                      {showCal && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {r.calibration_due
                              ? <span className={`font-mono text-[12px] ${days != null && days < 0 ? "text-danger" : days != null && days <= 30 ? "text-warn" : "text-text-muted"}`}>
                                  {fmtDate(r.calibration_due)}{days != null && days < 0 ? " · overdue" : ""}
                                </span>
                              : <span className="text-text-dim">{r.last_calibration_result === "fail" ? "failed — out of use" : "never calibrated"}</span>}
                            {r.category === "test_equipment" && canEdit && (
                              <button type="button" className="text-accent text-[12px] hover:underline inline-flex items-center gap-1" onClick={() => setCalFor(r)} title="Log a calibration — date, lab, certificate"><Gauge size={12}/> log</button>
                            )}
                          </div>
                          {r.last_calibrated_on && <div className="text-[10.5px] text-text-dim mt-0.5">last {fmtDate(r.last_calibrated_on)} · every {r.calibration_interval_months ?? 12} mo</div>}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <select value={r.assigned_to_employee_id ?? ""} onChange={e => void assign(r, e.target.value)}
                                className="bg-ink-raised border border-line rounded px-2 py-1 text-[12px] text-text outline-none focus:border-accent max-w-[160px]">
                          <option value="">— Pool —</option>
                          {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{`${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || "(no name)"}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{keptAt(r) || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" className="text-accent text-[12px] hover:underline" onClick={() => setEditing(r)}>{r.details_pending ? "Fill in" : "Edit"}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {calFor && (
        <CalibrationDialog asset={calFor} userId={userId} onClose={(saved) => { setCalFor(null); if (saved) refresh(); }} />
      )}
      {editing && (
        <EquipmentDialog
          row={editing === "new" ? null : editing}
          defaultCategory={cats[0].value}
          employees={employees}
          userId={userId}
          onClose={(saved) => { setEditing(null); if (saved) refresh(); }}
        />
      )}
    </>
  );
}

function EquipmentDialog({ row, defaultCategory, employees, userId, onClose }: {
  row: Row | null; defaultCategory: string; employees: Emp[]; userId: string | null; onClose: (saved: boolean) => void;
}) {
  const [category, setCategory] = useState(row?.category ?? defaultCategory);
  const [description, setDescription] = useState(row?.description ?? "");
  const [makeModel, setMakeModel] = useState(row?.make_model ?? "");
  const [serial, setSerial] = useState(row?.serial_number ?? "");
  const [kept, setKept] = useState<KeptAt>({ vehicleId: row?.kept_in_vehicle_id ?? null, location: row?.location ?? null });
  const [empId, setEmpId] = useState(row?.assigned_to_employee_id ?? "");
  const [calDue, setCalDue] = useState(row?.calibration_due ?? "");
  const [calMonths, setCalMonths] = useState(String(row?.calibration_interval_months ?? 12));
  const [notes, setNotes] = useState(row?.notes ?? "");
  const [active, setActive] = useState(row?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sel = "w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text outline-none focus:border-accent";

  async function save() {
    if (!description.trim()) { setErr("Say what the item is."); return; }
    setSaving(true); setErr(null);
    const payload: Record<string, any> = {
      category, description: description.trim(),
      make_model: makeModel.trim() || null, serial_number: serial.trim() || null,
      kept_in_vehicle_id: kept.vehicleId,
      location: kept.vehicleId ? null : ((kept.location ?? "").trim() || null),
      assigned_to_employee_id: empId || null,
      calibration_due: category === "test_equipment" ? (calDue || null) : null,
      calibration_interval_months: category === "test_equipment" ? (Number(calMonths) > 0 ? Number(calMonths) : 12) : 12,
      notes: notes.trim() || null, is_active: active,
      details_pending: false,   // someone has been through it
      updated_by: userId,
    };
    const q = (supabase as any).from("company_assets");
    const { error } = row
      ? await q.update(payload).eq("asset_id", row.asset_id)
      // added by hand here: not an insurance question unless the insurance register says so
      : await q.insert({ ...payload, created_by: userId, insurance_required: false, insurance_status: "not_required" });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onClose(true);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={() => onClose(false)}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-lg shadow-xl mb-10" onClick={e => e.stopPropagation()}>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-semibold text-text">{row ? `${row.asset_code ?? "Item"} — edit` : "Add an item"}</div>
            <button type="button" onClick={() => onClose(false)} className="text-text-muted hover:text-text"><X size={16} /></button>
          </div>
          {row?.ap_invoice_id && (
            <div className="text-[12px] text-text-muted">Came from an AP purchase{row.supplier_name ? ` — ${row.supplier_name}` : ""}{row.purchase_date ? `, ${fmtDate(row.purchase_date)}` : ""}.</div>
          )}
          <Field label="Which list">
            <select className={sel} value={category} onChange={e => setCategory(e.target.value)}>
              {EQUIPMENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="What it is">
            <Input value={description} onChange={(e: any) => setDescription(e.target.value)} placeholder="e.g. Handheld radio, Site laptop, OTDR" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Make / model"><Input value={makeModel} onChange={(e: any) => setMakeModel(e.target.value)} /></Field>
            <Field label="Serial / IMEI"><Input mono value={serial} onChange={(e: any) => setSerial(e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Who has it">
              <select className={sel} value={empId} onChange={e => setEmpId(e.target.value)}>
                <option value="">— Pool —</option>
                {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{`${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || "(no name)"}</option>)}
              </select>
            </Field>
            <Field label="Kept at"><KeptAtField value={kept} onChange={setKept} /></Field>
          </div>
          {category === "test_equipment" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Calibrate every" hint="Months. Log each calibration from the list — the due date rolls forward by itself.">
                <Input type="number" mono value={calMonths} onChange={(e: any) => setCalMonths(e.target.value)} />
              </Field>
              <Field label={row ? "Next due (from the last record)" : "Next due (if already calibrated)"} hint={row ? "Set by the calibration record; log one to move it." : "Leave blank and log the certificate once it's saved."}>
                <Input type="date" value={calDue} onChange={(e: any) => setCalDue(e.target.value)} disabled={!!row} />
              </Field>
            </div>
          )}
          <Field label="Notes"><Input value={notes} onChange={(e: any) => setNotes(e.target.value)} placeholder="optional" /></Field>
          {row && (
            <label className="flex items-center gap-2 text-[13px] text-text">
              <input type="checkbox" className="accent-accent" checked={active} onChange={e => setActive(e.target.checked)} />
              Still in use (untick when it is sold, lost or scrapped — the record stays)
            </label>
          )}
          {err && <div className="text-[12px] text-danger">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Log a calibration — mig 479. Date, who did it, certificate number and the
// PDF (asset-documents bucket), pass / adjusted / fail. The record is kept;
// the item's next due date follows. History shown underneath.
// =============================================================================
function CalibrationDialog({ asset, userId, onClose }: { asset: Row; userId: string | null; onClose: (saved: boolean) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [by, setBy] = useState("");
  const [certNo, setCertNo] = useState("");
  const [result, setResult] = useState<"pass" | "adjusted" | "fail">("pass");
  const [nextDue, setNextDue] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sel = "w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text outline-none focus:border-accent";

  const { data: history = [], refetch } = useQuery({
    queryKey: ["asset_calibrations", asset.asset_id],
    queryFn: async () => { const { data } = await (supabase as any).from("asset_calibrations").select("*").eq("asset_id", asset.asset_id).order("calibrated_on", { ascending: false }); return (data ?? []) as any[]; },
  });

  async function openCert(path: string) {
    const { data, error } = await supabase.storage.from("asset-documents").createSignedUrl(path, 600);
    if (error || !data?.signedUrl) { setErr(error?.message ?? "Couldn't open the certificate"); return; }
    window.open(data.signedUrl, "_blank");
  }

  async function save() {
    setErr(null);
    if (result === "fail" && !notes.trim()) { setErr("A fail needs a reason — what was wrong."); return; }
    setSaving(true);
    try {
      let certificate_path: string | null = null, certificate_name: string | null = null;
      if (file) {
        const safe = file.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
        const path = `calibration/${asset.asset_id}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from("asset-documents").upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
        if (upErr) throw new Error(`Saving the certificate failed: ${upErr.message}`);
        certificate_path = path; certificate_name = file.name;
      }
      const { error } = await (supabase as any).from("asset_calibrations").insert({
        asset_id: asset.asset_id, calibrated_on: date, calibrated_by: by.trim() || null, certificate_no: certNo.trim() || null,
        certificate_path, certificate_name, result, next_due: nextDue || null, notes: notes.trim() || null, logged_by: userId,
      });
      if (error) throw new Error(error.message);
      await refetch();
      onClose(true);
    } catch (e: any) { setErr(e?.message ?? String(e)); setSaving(false); }
  }

  const months = asset.calibration_interval_months ?? 12;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={() => onClose(false)}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-lg shadow-xl mb-10" onClick={e => e.stopPropagation()}>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div><div className="text-[15px] font-semibold text-text inline-flex items-center gap-2"><Gauge size={15} className="text-accent"/> Log a calibration</div><div className="text-[12px] text-text-muted">{asset.asset_code} · {asset.description}{asset.serial_number ? ` · ${asset.serial_number}` : ""} · every {months} months</div></div>
            <button type="button" onClick={() => onClose(false)} className="text-text-muted hover:text-text"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Calibrated on"><Input type="date" value={date} onChange={(e: any) => setDate(e.target.value)} /></Field>
            <Field label="Result">
              <select className={sel} value={result} onChange={e => setResult(e.target.value as any)}>
                <option value="pass">Pass — within tolerance</option>
                <option value="adjusted">Adjusted — brought back into tolerance</option>
                <option value="fail">Fail — out of use</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Calibrated by" hint="The lab or company on the certificate"><Input value={by} onChange={(e: any) => setBy(e.target.value)} placeholder="e.g. Narda Safety Test Solutions" /></Field>
            <Field label="Certificate no."><Input mono value={certNo} onChange={(e: any) => setCertNo(e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Certificate PDF" hint="Kept with the item.">
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => setFile(e.target.files?.[0] ?? null)} />
                <Button size="sm" variant="secondary" icon={Upload} onClick={() => fileRef.current?.click()}>{file ? file.name.slice(0, 24) : "Choose file"}</Button>
              </div>
            </Field>
            <Field label="Next due" hint={result === "fail" ? "Cleared on a fail" : `Blank = ${months} months on`}>
              <Input type="date" value={nextDue} onChange={(e: any) => setNextDue(e.target.value)} disabled={result === "fail"} />
            </Field>
          </div>
          <Field label={result === "fail" ? "What was wrong" : "Notes"}><Input value={notes} onChange={(e: any) => setNotes(e.target.value)} placeholder={result === "fail" ? "required" : "optional"} /></Field>
          {err && <div className="text-[12px] text-danger">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save the record"}</Button>
          </div>
          {history.length > 0 && (
            <div className="border-t border-line pt-3">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-semibold mb-1.5">History</div>
              <div className="space-y-1">
                {history.map((h: any) => (
                  <div key={h.calibration_id} className="text-[12px] flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{fmtDate(h.calibrated_on)}</span>
                    <Pill small tone={h.result === "fail" ? "critical" : h.result === "adjusted" ? "warning" : "ok"} label={h.result} />
                    {h.calibrated_by && <span className="text-text-muted">{h.calibrated_by}</span>}
                    {h.certificate_no && <span className="font-mono text-text-dim">{h.certificate_no}</span>}
                    {h.certificate_path && <button type="button" className="text-accent hover:underline inline-flex items-center gap-1" onClick={() => openCert(h.certificate_path)}><FileText size={11}/> certificate</button>}
                    {h.next_due && <span className="text-text-dim">→ due {fmtDate(h.next_due)}</span>}
                    {h.notes && <span className="text-text-dim">· {h.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
